import React, { useEffect, useState } from 'react';
import { Select, SelectItem } from '@nextui-org/react';
import { getAvailableCountries } from 'common/src/api/settlements';
import useUserStore from '../../stores/useUserStore';
import useFilterStore from '../../stores/useFiltersStore';
import useDataStore from '../../stores/useDataStore';

interface CountryFilterProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  onCountriesLoaded?: (mostCommon: string | null) => void;
}

const CountryFilter: React.FC<CountryFilterProps> = ({
  value,
  onChange,
  disabled,
  className,
  onCountriesLoaded,
}) => {
  const { session } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [countries, setCountries] = useState<string[]>([]);
  const [countriesWithCount, setCountriesWithCount] = useState<
    Array<{ country: string; count: number }>
  >([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      if (!session?.access_token || !selectedSeason || !selectedSpecies?.id)
        return;

      setLoading(true);
      try {
        const result = await getAvailableCountries(
          session,
          selectedSeason,
          selectedSpecies.id
        );
        setCountries(result.countries);
        setCountriesWithCount(result.countriesWithCount);

        // Notify parent about the most common country
        if (onCountriesLoaded) {
          onCountriesLoaded(result.mostCommon);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [
    session?.access_token,
    selectedSeason,
    selectedSpecies?.id,
    onCountriesLoaded,
  ]);

  // Ensure the value exists in available options
  const availableKeys = ['all', ...countriesWithCount.map((c) => c.country)];
  const validValue = value && availableKeys.includes(value) ? value : '';

  return (
    <Select
      label='País'
      placeholder='Seleccionar país'
      selectedKeys={validValue ? new Set([validValue]) : new Set()}
      onSelectionChange={(keys) => {
        const selectedValue = Array.from(keys)[0] as string;
        onChange(selectedValue || 'all');
      }}
      className={className}
      isDisabled={disabled || loading}
      description={loading ? 'Cargando países...' : ''}
      items={[
        { key: 'all', label: 'Todos los países' },
        ...countriesWithCount.map(({ country }) => ({
          key: country,
          label: country,
        })),
      ]}
    >
      {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
    </Select>
  );
};

export default CountryFilter;
